<template>
  <section :class="{ 'war-room-post-author': true, 'war-room-post-author__disabled': !profileUrl }">
    <component :is="linkComponent" v-if="post.author" class="war-room-post-author__link" :to="profileUrl">
      <div v-if="!profileUrl" class="war-room-post-author__link__hint">
        <BravadoText class="war-room-post-author__link__hint-text" tag="p" variant="subhead-semibold">
          {{ disabledLinkHint }}
        </BravadoText>
      </div>
      <Avatar
        class="war-room-post-author__avatar"
        type="next-medium"
        :alt="`${username} profile picture`"
        :title="initials"
        :src="post.author.avatar_url"
        loading="lazy"
      />
      <div>
        <div class="war-room-post-author__info">
          <Avatar
            class="war-room-post-author__avatar-mini"
            type="next-tiny"
            :alt="`${username} profile picture`"
            :title="initials"
            :src="post.author.avatar_url"
          />
          <BravadoText class="war-room-post-author__title" variant="subhead-semibold" tag="h3">
            {{ username }}
          </BravadoText>
          <BravadoText v-if="position" class="war-room-post-author__position" variant="subhead" tag="p">
            {{ position }}
          </BravadoText>
        </div>
        <div v-if="achievements.length" class="war-room-post-author__achievements">
          <WarRoomAchievement
            v-for="{ title, level } in achievements"
            :key="title"
            :title="title"
            :level="level"
            :size="achievementSize"
          />
          <WarRoomAchievement
            v-if="additionalAchievement"
            :title="additionalAchievement.title"
            :level="additionalAchievement.level"
            :size="achievementSize"
          />
        </div>
      </div>
    </component>
    <WarRoomPostActions :post="post" class="war-room-post-author__menu" />
  </section>
</template>

<script lang="ts" setup>
import { useContext, computed } from '@nuxtjs/composition-api';
import Avatar from '@/components/common/Avatar/Avatar.vue';
import WarRoomAchievement from '@/components/partials/war-room/WarRoomAchievement.vue';
import WarRoomPostActions from '@/components/partials/war-room/WarRoomPostActions.vue';
import { achievementSizes } from '@/components/partials/war-room/constants';
import { initials as initialsUtils } from '@/components/utils/core/string';
import type { IPost } from '@/types/community';

type WarRoomPostAuthorProps = {
  post: IPost;
};

const props = defineProps<WarRoomPostAuthorProps>();

const { $device, $auth } = useContext();

const achievementSize = achievementSizes.SMALL;
const maxVisibleAchievements = $device.isMobile ? 2 : 3;

const profileUrl = computed(() => {
  if (props.post.is_anonymous) {
    return `/war-room/profile/${props.post.author?.username}`;
  }
  const isSameUser = props.post.author?.slug === $auth.user?.slug && props.post.author?.slug;
  if (props.post.author?.portfolio_configuration?.is_public || isSameUser) {
    return `/p/${props.post.author?.slug}`;
  }
});

const username = computed(() => {
  if (props.post.is_anonymous) return props.post.author?.username;
  if (!profileUrl.value) return `${props.post.author?.firstname} ${props.post.author?.lastname?.substring(0, 1)}`;
  return `${props.post.author?.firstname} ${props.post.author?.lastname}`;
});

const position = computed(() => {
  if (!props.post.author?.position) return '';

  let position = props.post.author.position;

  if (props.post.author.current_company) {
    position += ` at ${props.post.author.current_company}`;
  }

  return position;
});

const disabledLinkHint = computed(() => {
  if (props.post.author?.firstname) {
    return `${props.post.author?.firstname?.trim()}'s profile is not public`;
  }

  return `Author's profile is not public`;
});

const initials = computed(() => initialsUtils(username.value));
const achievements = computed(() => props.post.author?.achievements?.slice(0, maxVisibleAchievements) || []);
const additionalAchievement = computed(() => {
  const remainingCount = Math.max(0, (props.post.author?.achievements?.length || 0) - maxVisibleAchievements);
  if (remainingCount < 1) return undefined;
  return {
    title: `+${remainingCount}`,
    slug: '',
    level: 'no_level',
  };
});
const linkComponent = computed(() => (profileUrl.value ? 'nuxt-link' : 'a'));
</script>

<style scoped lang="scss">
.war-room-post-author {
  $this: &;

  display: flex;
  align-items: center;
  width: 100%;

  &__disabled {
    position: relative;

    &:hover,
    &:focus {
      #{$this}__link__hint {
        display: block;
      }
    }
  }

  &__link {
    $this2: &;

    margin-right: 8px;
    display: flex;
    align-items: center;
    outline: none;

    &:hover,
    &:focus {
      cursor: pointer;

      #{$this}__title {
        text-decoration: underline;
      }
    }

    &__hint {
      display: none;
      position: absolute;
      padding-top: 6px;
      top: calc(100% + 6px);
      z-index: 99;
      left: 0;

      &-text {
        position: relative;
        padding: 8px;
        color: var(--theme-color-card);
        background-color: var(--theme-color-text);
        border-radius: 8px;

        &::before {
          content: '';
          position: absolute;
          left: 14px;
          top: -4px;
          width: 12px;
          height: 12px;
          transform: rotate(45deg);
          background-color: var(--theme-color-text);
        }
      }
    }
  }

  &__avatar {
    display: none !important;

    @include mq('sm') {
      display: flex !important;
      margin-right: 12px;
    }
  }

  &__info {
    display: flex;
    flex-wrap: wrap;

    &:not(:last-child) {
      margin-bottom: 4px;
    }

    @include mq('sm') {
      flex-wrap: nowrap;
      align-items: center;
    }
  }

  &__avatar-mini {
    margin-right: 8px;

    @include mq('sm') {
      display: none !important;
    }
  }

  &__title {
    color: var(--theme-color-text);
  }

  &__position {
    width: 100%;
    margin-top: 4px;
    color: var(--theme-color-sub-text);

    @include mq('sm') {
      position: relative;
      width: auto;
      margin: 0;
      padding-left: 16px;

      &::before {
        content: '';
        position: absolute;
        top: calc(50% - 2px);
        left: 7px;
        width: 2px;
        height: 2px;
        background-color: var(--theme-color-sub-text);
        border-radius: 50%;
      }
    }
  }

  &__achievements {
    display: flex;
    align-items: center;
    grid-gap: 4px;
  }

  &__menu {
    margin-left: auto;
    flex-shrink: 0;
    color: var(--gray-50);
  }
}
</style>
